// Pure domain helpers of the CTC assistant: no Vue, no network, no alias
// import, so the rules can be exercised outside the app.

import {
  DEAL_PRICES_MESSAGE,
  EDITORIAL_FORMAT_DEAL,
  EDITORIAL_FORMAT_FOCUS,
  GENERATION_BLOCK_LABELS,
  MERCHANTS,
  REQUESTED_PUBLICATION_LABELS,
  SCHEDULE_STEP_MINUTES,
  TITLE_MAX_LENGTH,
  WIDGET_FORMATS,
} from "./constants";

/**
 * @typedef {"merchant_page"|"affilizz"|"manual"} Provenance
 *
 * @typedef {Object} Site - RMR site with an Affilizz media and a CMS connector
 * @property {number} id
 * @property {string} name
 * @property {string} domain
 * @property {string} affilizzMediaName
 * @property {string} defaultSignature
 *
 * @typedef {Object} Product
 * @property {string} id
 * @property {string} sourceUrl
 * @property {"amazon"|"fnac"} merchant
 * @property {string} merchantProductId
 * @property {string} [productLocalizedId] - set for Affilizz opportunities
 * @property {string} title
 * @property {{ icon: string, color: string }} visual
 * @property {number|null} priceCurrent
 * @property {number|null} priceOriginal
 * @property {"in_stock"|"out_of_stock"|"unknown"} availability
 * @property {number|null} rating
 * @property {number|null} reviewCount
 * @property {string[]} features
 * @property {Object<string, Provenance>} provenance - one entry per field above
 *
 * @typedef {Object} OfferCheck - read-only step-1 check of the main product
 * @property {string} productId
 * @property {number} siteId
 * @property {boolean} hasOffer
 * @property {string|null} reason - why no LINK can be created
 *
 * @typedef {Object} AffilizzWrite - technical outcome of one Affilizz write
 *   (anti-duplicate safety, not an affiliation status)
 * @property {"creating"|"created"|"error"|"unknown"} status
 * @property {string|null} reason
 * @property {boolean} retryable - certain failure or interrupted creation
 * @property {string|null} contentId
 * @property {string|null} [tinyCode] - LINK only
 *
 * @typedef {Object} AffilizzPublication - one publication per draft
 * @property {string|null} publicationId
 * @property {AffilizzWrite} link
 * @property {AffilizzWrite|null} widget - only once the widget was attempted
 *
 * @typedef {Object} Opportunity
 * @property {string} id
 * @property {string} detectedAt - ISO date
 * @property {{ type: "price_drop", label: string }} signal
 * @property {Product} product
 * @property {"new"|"used"|"ignored"} status
 * @property {number|null} articleId
 *
 * @typedef {Object} Generation - transient hand-over to AifPostGeneration,
 *   returned by a launch or a retry only
 * @property {"queued"|"blocked"|"waiting_affilizz"} status
 * @property {string|null} code - why nothing was queued
 * @property {string|null} message - what to do before a new try
 * @property {boolean} retryable
 * @property {number|null} aifQueueId
 * @property {string[]} warnings
 *
 * @typedef {Object} Article - persisted RMR draft (GET /articles)
 * @property {number} id
 * @property {number} siteId
 * @property {"amazon"|"fnac"} merchant
 * @property {string} merchantProductId
 * @property {string} productTitle
 * @property {string} signature
 * @property {"draft"|"publish"|"future"} postStatus - requested, not done yet
 * @property {string|null} scheduledAt
 * @property {Object|null} productSnapshot
 * @property {boolean} widgetEnabled
 * @property {"BOX"|"CARD"} widgetType
 * @property {SimilarProductGroup[]} similarProducts
 * @property {string|null} siteName - kept even if the site is no longer eligible
 * @property {string|null} siteDomain
 * @property {AffilizzPublication|null} affilizz
 * @property {number|null} aifQueueId - the AifPostGeneration request, once queued
 * @property {Generation|null} [generation]
 * @property {string} createdAt
 * @property {string} updatedAt
 *
 * @typedef {Object} SimilarProduct - immutable Affilizz search snapshot
 * @property {string} productLocalizedId
 * @property {string|null} productId
 * @property {string} title
 * @property {string|null} imageUrl
 * @property {number|null} minPrice
 * @property {number|null} maxPrice
 * @property {string|null} currency
 * @property {number} offerCount
 *
 * @typedef {Object} SimilarProductGroup - one BOX tab and its color variants
 * @property {string} title
 * @property {SimilarProduct[]} products
 */

export class CtcServiceError extends Error {
  /**
   * @param {string} code - backend error code, or a local fallback code
   * @param {string} message
   */
  constructor(code, message, details = {}) {
    super(message);
    this.name = "CtcServiceError";
    this.code = code;
    // Extra fields of the backend refusal, e.g. `affiliation`.
    this.details = details;
  }
}

export const PRODUCT_FIELDS = [
  "title",
  "priceCurrent",
  "priceOriginal",
  "availability",
  "rating",
  "reviewCount",
  "features",
];

export const EDITABLE_PRODUCT_FIELDS = [
  "title",
  "priceCurrent",
  "priceOriginal",
  "availability",
];

export const clone = (value) =>
  value === undefined ? undefined : JSON.parse(JSON.stringify(value));

// A price is shown in its own currency; without a known one it is shown as a
// bare amount, never as euros.
const amountFormatter = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const currencyFormatters = new Map();

const currencyFormatter = (currency) => {
  const code =
    typeof currency === "string" ? currency.trim().toUpperCase() : "";
  if (!/^[A-Z]{3}$/.test(code)) return null;
  if (!currencyFormatters.has(code)) {
    let formatter = null;
    try {
      formatter = new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: code,
      });
    } catch (error) {
      formatter = null;
    }
    currencyFormatters.set(code, formatter);
  }
  return currencyFormatters.get(code);
};

const dateTimeFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const formatPrice = (value, currency) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const formatter = currencyFormatter(currency);
  return formatter ? formatter.format(value) : amountFormatter.format(value);
};

/** Symbol of a price input ("€", "$US"…), empty when the currency is unknown. */
export const currencySymbol = (currency) => {
  const formatter = currencyFormatter(currency);
  const part =
    formatter &&
    formatter.formatToParts(0).find(({ type }) => type === "currency");
  return part ? part.value : "";
};

/** Lowest price of an Affilizz product, or where the widget shows it. */
export const similarPriceLabel = (product) =>
  typeof product.minPrice === "number"
    ? `Dès ${formatPrice(product.minPrice, product.currency)}`
    : "Prix communiqué dans le widget";

/** "3 modèles · 6 produits": the selection itself, whatever the format. */
export const selectionCountLabel = (groups = []) => {
  const products = groups.reduce(
    (sum, group) => sum + group.products.length,
    0
  );
  if (!products) return "Aucun produit sélectionné";
  const count = (value, word) => `${value} ${word}${value > 1 ? "s" : ""}`;
  return `${count(groups.length, "modèle")} · ${count(products, "produit")}`;
};

/**
 * The widget as it is sent to Affilizz: BOX one tab per model (its colours
 * inside), CARD a single card holding every product in selection order. The
 * groups are kept as they are in both formats; only their reading changes.
 */
export const widgetStructureOf = (widgetType, groups = []) => {
  const type = widgetType === "CARD" ? "CARD" : "BOX";
  const products = groups.flatMap((group) => group.products);
  const count = (value, word) => `${value} ${word}${value > 1 ? "s" : ""}`;
  const format = WIDGET_FORMATS.find((item) => item.value === type);
  let summary = "Aucun produit sélectionné.";
  if (products.length) {
    summary =
      type === "BOX"
        ? `${count(groups.length, "onglet")} · ${count(
            products.length,
            "produit"
          )}`
        : `1 carte · ${count(products.length, "produit")}`;
  }
  return {
    type,
    name: format.name,
    summary,
    tabs:
      type === "BOX"
        ? groups.map((group, index) => ({
            label: `Onglet ${index + 1}`,
            title: group.title,
            products: group.products,
          }))
        : [],
    products,
  };
};

export const offerCountLabel = (count) => {
  const plural = Number(count) === 1 ? "" : "s";
  return `${count || 0} offre${plural} référencée${plural}`;
};

export const formatDateTime = (iso) =>
  iso ? dateTimeFormatter.format(new Date(iso)) : "—";

/**
 * The CMS publication a draft asks for, phrased as a future action: nothing
 * is sent before the generation. `scheduledAt` is a Date or an ISO string.
 */
export const requestedPublicationLabel = (postStatus, scheduledAt = null) => {
  const label =
    REQUESTED_PUBLICATION_LABELS[postStatus] ||
    REQUESTED_PUBLICATION_LABELS.draft;
  if (postStatus !== "future" || !scheduledAt) return label;
  const iso =
    scheduledAt instanceof Date ? scheduledAt.toISOString() : scheduledAt;
  return `${label}, le ${formatDateTime(iso)}`;
};

const pad = (value) => String(value).padStart(2, "0");

/** "YYYY-MM-DD" of a date in the browser's time zone. */
export const localDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Every slot of the day; for today, only the slots still to come. */
export const scheduleTimeOptions = (
  scheduledDate,
  scheduledTime,
  now = new Date()
) => {
  const isToday = scheduledDate === localDateKey(now);
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  const options = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += SCHEDULE_STEP_MINUTES) {
    if (isToday && minutes <= minutesNow) continue;
    const value = `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
    options.push({ value, text: value });
  }
  // Keep a manually set value selectable even if it is not a slot.
  if (scheduledTime && !options.some(({ value }) => value === scheduledTime)) {
    options.unshift({ value: scheduledTime, text: scheduledTime });
  }
  return options;
};

export const merchantLabel = (merchant) =>
  (MERCHANTS[merchant] && MERCHANTS[merchant].label) || merchant || "—";

/** Discount is always derived from the prices, never stored. */
export const discountPercent = (product) => {
  if (!product) return null;
  const { priceCurrent, priceOriginal } = product;
  if (
    typeof priceCurrent !== "number" ||
    typeof priceOriginal !== "number" ||
    !(priceCurrent > 0) ||
    !(priceOriginal > priceCurrent)
  ) {
    return null;
  }
  return Math.round((1 - priceCurrent / priceOriginal) * 100);
};

/**
 * A Bon plan needs a real price drop in a known currency, as on the backend.
 * It is never switched to Focus produit automatically.
 * @returns {string|null} the message to display, or null
 */
export const dealPriceProblem = (product) => {
  const { priceCurrent, priceOriginal, currency } = product || {};
  const positive = (value) =>
    typeof value === "number" && Number.isFinite(value) && value > 0;
  return positive(priceCurrent) &&
    positive(priceOriginal) &&
    priceOriginal > priceCurrent &&
    typeof currency === "string" &&
    /^[A-Z]{3}$/.test(currency.trim().toUpperCase())
    ? null
    : DEAL_PRICES_MESSAGE;
};

/** Bon plan by default only for an opportunity whose prices already prove it. */
export const defaultEditorialFormat = (product, fromOpportunity) =>
  fromOpportunity && !dealPriceProblem(product)
    ? EDITORIAL_FORMAT_DEAL
    : EDITORIAL_FORMAT_FOCUS;

/**
 * Format check only (the merchant check belongs to the analysis).
 * @returns {{ code: string, message: string }|null}
 */
export const validateSourceUrl = (rawUrl) => {
  const value = (rawUrl || "").trim();
  if (!value) {
    return { code: "empty", message: "Saisissez l’URL d’une fiche produit." };
  }
  let url;
  try {
    url = new URL(value);
  } catch (error) {
    url = null;
  }
  if (!url || !["http:", "https:"].includes(url.protocol) || !url.hostname) {
    return {
      code: "invalid_url",
      message: "URL invalide : elle doit commencer par http:// ou https://.",
    };
  }
  return null;
};

/** @returns {"amazon"|"fnac"|null} */
export const detectMerchant = (rawUrl) => {
  let hostname;
  try {
    hostname = new URL((rawUrl || "").trim()).hostname.toLowerCase();
  } catch (error) {
    return null;
  }
  return (
    Object.keys(MERCHANTS).find((key) => {
      const { domain } = MERCHANTS[key];
      return hostname === domain || hostname.endsWith(`.${domain}`);
    }) || null
  );
};

// Same rule as the backend: a signature is plain text placed in the article
// HTML, without markup or control character.
// eslint-disable-next-line no-control-regex
const SIGNATURE_FORBIDDEN_PATTERN = /[<>\u0000-\u001f\u007f-\u009f]/;

export const SIGNATURE_PLAIN_TEXT_MESSAGE =
  "La signature doit être un texte simple, sans balise HTML ni caractère de contrôle.";

export const isPlainTextSignature = (value) =>
  !SIGNATURE_FORBIDDEN_PATTERN.test(value || "");

/** "12,50" → 12.5 ; "" → null ; anything else not numeric → NaN. */
export const parsePriceInput = (raw) => {
  if (raw === null || raw === undefined) return null;
  const value = String(raw).trim().replace(/\s/g, "").replace(",", ".");
  if (!value) return null;
  return /^\d+(\.\d{1,2})?$/.test(value) ? Number(value) : NaN;
};

/** @returns {Object<string, string>} field → error message */
export const validateProduct = (product) => {
  const errors = {};
  if (!product) return errors;

  const title = (product.title || "").trim();
  if (!title) {
    errors.title = "Le titre est obligatoire.";
  } else if (title.length > TITLE_MAX_LENGTH) {
    errors.title = `${TITLE_MAX_LENGTH} caractères maximum.`;
  }

  const { priceCurrent, priceOriginal } = product;
  const hasCurrent = typeof priceCurrent === "number" && priceCurrent > 0;
  if (!hasCurrent) {
    errors.priceCurrent = "Le prix actuel doit être supérieur à 0.";
  }
  if (priceOriginal !== null && priceOriginal !== undefined) {
    if (!(typeof priceOriginal === "number" && priceOriginal > 0)) {
      errors.priceOriginal = "Ancien prix invalide (laisser vide si aucun).";
    } else if (hasCurrent && !(priceOriginal > priceCurrent)) {
      errors.priceOriginal =
        "L’ancien prix doit être supérieur au prix actuel.";
    }
  }
  return errors;
};

/** Returns a new product; an actual change marks the field as manual. */
export const applyProductEdit = (product, field, value) => {
  if (!product || !EDITABLE_PRODUCT_FIELDS.includes(field)) return product;
  if (product[field] === value) return product;
  return {
    ...product,
    [field]: value,
    provenance: { ...product.provenance, [field]: "manual" },
  };
};

/**
 * An analysis started from an opportunity keeps what the (hypothetical)
 * Affilizz signal provided: title and prices keep the Affilizz provenance.
 */
export const mergeOpportunityData = (analyzedProduct, opportunityProduct) => {
  if (!opportunityProduct) return analyzedProduct;
  const merged = clone(analyzedProduct);
  Object.keys(opportunityProduct.provenance || {}).forEach((field) => {
    if (opportunityProduct.provenance[field] === "affilizz") {
      merged[field] = clone(opportunityProduct[field]);
      merged.provenance[field] = "affilizz";
    }
  });
  return merged;
};

const ISSUE_LABELS = {
  link: {
    error: "Échec du lien Affilizz",
    unknown: "Lien Affilizz à contrôler",
    creating: "Création du lien Affilizz interrompue",
  },
  widget: {
    error: "Échec du widget Affilizz",
    unknown: "Widget Affilizz à contrôler",
    creating: "Création du widget Affilizz interrompue",
  },
};

/**
 * The Affilizz write of a draft that needs an action: a certain failure, an
 * uncertain write or an interrupted creation. A created or running write is
 * not reported.
 * @returns {{label: string, reason: string|null, retryable: boolean}|null}
 */
export const affilizzIssueOf = (article) => {
  const affilizz = article && article.affilizz;
  if (!affilizz) return null;
  if (
    article.widgetEnabled &&
    affilizz.link &&
    affilizz.link.status === "created" &&
    !affilizz.widget
  ) {
    return {
      label: "Widget Affilizz non créé",
      reason: "La création du widget n’a pas démarré.",
      retryable: true,
    };
  }
  const issue = [
    ["link", affilizz.link],
    ["widget", affilizz.widget],
  ].find(
    ([, write]) =>
      write && (["error", "unknown"].includes(write.status) || write.retryable)
  );
  if (!issue) return null;
  const [kind, write] = issue;
  return {
    label: ISSUE_LABELS[kind][write.status],
    reason: write.reason || null,
    retryable: !!write.retryable,
  };
};

const contentsCreated = (article) => {
  const { link, widget } = (article && article.affilizz) || {};
  return (
    !!link &&
    link.status === "created" &&
    (!article.widgetEnabled || (!!widget && widget.status === "created"))
  );
};

/** A scheduled date already passed: the draft must be rescheduled. */
export const isScheduleExpired = (article, now = Date.now()) =>
  !!article &&
  article.postStatus === "future" &&
  !!article.scheduledAt &&
  new Date(article.scheduledAt).getTime() <= now;

/**
 * Generation state of a draft. A launch or a retry returns `generation`; the
 * list only knows `aifQueueId`, so a draft with created contents and no AIF
 * request offers a new try.
 * @returns {{kind: "queued"|"blocked"|"waiting"|"none", label: string,
 *   message: string|null, code: string|null, aifQueueId: number|null,
 *   retryable: boolean, reschedulable: boolean}}
 */
export const generationStateOf = (article) => {
  const generation = (article && article.generation) || null;
  const aifQueueId =
    (article && article.aifQueueId) ||
    (generation && generation.aifQueueId) ||
    null;
  if (aifQueueId) {
    return {
      kind: "queued",
      label: "Transmis à la génération",
      message: null,
      code: null,
      aifQueueId,
      retryable: false,
      reschedulable: false,
    };
  }
  if (!contentsCreated(article)) {
    return {
      kind: "waiting",
      label: "En attente des contenus Affilizz",
      message: null,
      code: null,
      aifQueueId: null,
      retryable: false,
      reschedulable: false,
    };
  }
  const expired =
    isScheduleExpired(article) ||
    (!!generation && generation.code === "ctc_schedule_expired");
  if (generation && generation.status === "blocked") {
    return {
      kind: "blocked",
      label: GENERATION_BLOCK_LABELS[generation.code] || "Génération bloquée",
      message: generation.message || null,
      code: generation.code || null,
      aifQueueId: null,
      retryable: generation.retryable !== false,
      reschedulable: expired,
    };
  }
  return {
    kind: "none",
    label: expired
      ? GENERATION_BLOCK_LABELS.ctc_schedule_expired
      : "Génération non lancée",
    message: expired
      ? "Reprogrammez l’article, puis relancez la génération."
      : "Relancez la vérification pour transmettre l’article à la génération.",
    code: expired ? "ctc_schedule_expired" : null,
    aifQueueId: null,
    retryable: true,
    reschedulable: expired,
  };
};

/**
 * The AIF PostGeneration list searched on the request id of a draft, through
 * its existing "Topic / ID" search (which may also match a WordPress post id
 * or a topic); null while no request exists.
 * @param {string} searchParam - URL_PARAM_NAMES.Search of the app
 */
export const aifGenerationRoute = (article, searchParam) => {
  const aifQueueId =
    (article && article.aifQueueId) ||
    (article && article.generation && article.generation.aifQueueId) ||
    null;
  if (!aifQueueId) return null;
  return {
    name: "ai-factory-post-generation-list",
    query: {
      [searchParam]: String(aifQueueId),
      site_id: String(article.siteId),
    },
  };
};

/**
 * Identity of what decides the Affilizz contents (site, main product and its
 * title, which is the public widget title, widget composition). Any change
 * makes a previous verification stale.
 */
export const selectionKey = ({
  siteId,
  product,
  widgetEnabled,
  widgetType,
  similarSelected,
}) => {
  if (!product || !siteId) return null;
  return JSON.stringify([
    siteId,
    product.merchant || "",
    product.merchantProductId || "",
    product.productLocalizedId || "",
    (product.title || "").trim(),
    !!widgetEnabled,
    widgetEnabled ? widgetType : null,
    widgetEnabled
      ? (similarSelected || []).map((group) => [
          group.title,
          group.products.map((item) => item.productLocalizedId),
        ])
      : [],
  ]);
};

/** One stored Affilizz state; null (still checking) is never "not affiliated". */
export const affiliationStateOf = (value) => {
  if (value === true) return "affiliated";
  if (value === false) return "not_affiliated";
  return "pending";
};

const MAIN_NOT_AFFILIATED = {
  tone: "error",
  title: "Produit principal non affilié",
  message:
    "Affilizz indique que le produit principal n’est pas affilié. Cet article ne peut pas être généré avec ce produit.",
  actions: ["choose-product"],
  canContinue: false,
};

/**
 * What the verification step says and offers, from the CTA and widget states
 * the backend returned (true, false, or null while Affilizz still checks).
 * A literal false is Affilizz's final answer: it is never offered a re-read,
 * and the widget never compensates for the main product.
 *
 * @param {{link: ?boolean, widget: ?boolean, widgetIncluded: boolean}} affiliation
 * @returns {{tone: string, title: string, message: string,
 *   actions: Array<"recheck"|"disable-widget"|"edit-widget"|"choose-product">,
 *   canContinue: boolean}}
 */
export const affiliationGuidance = (affiliation) => {
  const { link, widget, widgetIncluded } = affiliation || {};
  if (link === false) return MAIN_NOT_AFFILIATED;
  if (link !== true) {
    return {
      tone: "warning",
      title: "Affiliation du produit en cours de vérification",
      message:
        "Affilizz n’a pas encore confirmé l’affiliation du produit principal. Vous pouvez relire son état plus tard ou choisir un autre produit.",
      actions: ["recheck", "choose-product"],
      canContinue: false,
    };
  }
  if (!widgetIncluded) {
    return {
      tone: "success",
      title: "Produit principal affilié",
      message:
        "Le produit principal est affilié. Le widget est désactivé : il ne sera pas intégré dans l’article. Vous pouvez continuer.",
      actions: [],
      canContinue: true,
    };
  }
  if (widget === false) {
    return {
      tone: "error",
      title: "Widget comparatif non affilié",
      message:
        "Le produit principal est affilié, mais aucun produit du widget comparatif n’est affilié. Modifiez le widget ou continuez sans widget.",
      actions: ["disable-widget", "edit-widget"],
      canContinue: false,
    };
  }
  if (widget !== true) {
    return {
      tone: "warning",
      title: "Vérification du widget en cours",
      message:
        "Le produit principal est affilié. Affilizz n’a pas encore confirmé l’affiliation du widget. Vous pouvez attendre et relire son état, modifier le widget ou continuer sans widget.",
      actions: ["recheck", "disable-widget", "edit-widget"],
      canContinue: false,
    };
  }
  return {
    tone: "success",
    title: "Affiliation confirmée",
    message:
      "Le produit principal et le widget sont affiliés. Vous pouvez continuer.",
    actions: [],
    canContinue: true,
  };
};

/**
 * State key of one component card: the widget has a fourth state when it is
 * not part of the article.
 */
export const componentStateOf = (value, included = true) =>
  included ? affiliationStateOf(value) : "disabled";

/** Backend datetimes are naive UTC ("2026-10-30T10:00:00"). */
export const parseUtcDate = (value) => {
  if (!value) return null;
  const text = String(value);
  const date = new Date(
    /(?:[zZ]|[+-]\d\d:?\d\d)$/.test(text) ? text : `${text}Z`
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * One status for a creation to resume, from the same component states as
 * step 2: a required "false" is the problem to fix, anything still null is
 * waiting for Affilizz, otherwise the creation can continue.
 * @returns {{tone: "ready"|"pending"|"action", label: string}}
 */
export const pendingStatusOf = (affiliation) => {
  const { link, widget, widgetIncluded } = affiliation || {};
  if (link === false) {
    return { tone: "action", label: "Produit principal non affilié" };
  }
  if (widgetIncluded && widget === false) {
    return { tone: "action", label: "Widget comparatif non affilié" };
  }
  if (link !== true || (widgetIncluded && widget !== true)) {
    return { tone: "pending", label: "Vérification Affilizz en cours" };
  }
  return { tone: "ready", label: "Prêt à continuer" };
};

const timeFormatter = new Intl.DateTimeFormat("fr-FR", {
  hour: "2-digit",
  minute: "2-digit",
});
const dayFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
});
const dayYearFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/**
 * "aujourd’hui à 14:32", "hier à 09:05", "12 oct. à 14:32" (with the year
 * when it is not the current one), in the browser's time zone. The API
 * datetimes are UTC without offset (parseUtcDate).
 */
export const resumeDateLabel = (value, now = new Date()) => {
  const date = parseUtcDate(value);
  if (!date) return "";
  const time = timeFormatter.format(date);
  const dayKey = localDateKey(date);
  if (dayKey === localDateKey(now)) return `aujourd’hui à ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey === localDateKey(yesterday)) return `hier à ${time}`;
  const formatter =
    date.getFullYear() === now.getFullYear() ? dayFormatter : dayYearFormatter;
  return `${formatter.format(date)} à ${time}`;
};

/**
 * What changed since the verified draft, for "Vérification à relancer": the
 * main product, and the widget changes in plain words. Only the stored
 * selection is compared; nothing is asked to Affilizz.
 */
export const verificationChangesOf = (article, current) => {
  if (!article) return { product: false, widget: [] };
  const was = article.product || article.productSnapshot || {};
  const now = current.product || {};
  const product =
    article.siteId !== current.siteId ||
    (was.title || "").trim() !== (now.title || "").trim() ||
    (was.merchantProductId || null) !== (now.merchantProductId || null);
  const widget = [];
  if (!!article.widgetEnabled !== !!current.widgetEnabled) {
    widget.push(current.widgetEnabled ? "widget activé" : "widget désactivé");
  } else if (current.widgetEnabled) {
    if (article.widgetType !== current.widgetType) {
      widget.push(`format ${article.widgetType} → ${current.widgetType}`);
    }
    const before = (article.similarProducts || []).flatMap((g) => g.products);
    const after = (current.groups || []).flatMap((g) => g.products);
    const ids = (list) => new Set(list.map((p) => p.productLocalizedId));
    const beforeIds = ids(before);
    const afterIds = ids(after);
    const added = after.filter((p) => !beforeIds.has(p.productLocalizedId));
    const removed = before.filter((p) => !afterIds.has(p.productLocalizedId));
    const plural = (n, word) => `${n} ${word}${n > 1 ? "s" : ""}`;
    if (added.length) {
      widget.push(
        `+${plural(added.length, "produit")} (${added[0].title}${
          added.length > 1 ? "…" : ""
        })`
      );
    }
    if (removed.length) {
      widget.push(
        `−${plural(removed.length, "produit")} retiré${
          removed.length > 1 ? "s" : ""
        }`
      );
    }
    const titlesBefore = new Map(
      (article.similarProducts || []).map((g) => [
        g.products[0] && g.products[0].productLocalizedId,
        g.title,
      ])
    );
    if (
      (current.groups || []).some(
        (g) =>
          titlesBefore.has(g.products[0].productLocalizedId) &&
          titlesBefore.get(g.products[0].productLocalizedId) !== g.title
      )
    ) {
      widget.push("titre d’onglet renommé");
    }
  }
  return { product, widget };
};

/** "à l’instant", "il y a 2 min", then the resume date label. */
export const checkedAgoLabel = (value, now = Date.now()) => {
  const date = parseUtcDate(value);
  if (!date) return "";
  const minutes = Math.floor((now - date.getTime()) / 60000);
  if (minutes < 1) return "à l’instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  return resumeDateLabel(value, new Date(now));
};

/** Toast after a launch or a retry: Affilizz contents, then the generation. */
export const launchNoticeOf = (article) => {
  const issue = affilizzIssueOf(article);
  if (issue) {
    return {
      color: "warning",
      message: `Brouillon RMR enregistré. ${issue.label}${
        issue.reason ? ` : ${issue.reason}` : "."
      }`,
    };
  }
  if (!contentsCreated(article)) {
    return {
      color: "info",
      message: "Brouillon RMR enregistré ; la création Affilizz est en cours.",
    };
  }
  const generation = generationStateOf(article);
  const warnings = ((article.generation && article.generation.warnings) || [])
    .map((warning) => ` ${warning}`)
    .join("");
  if (generation.kind === "queued") {
    return {
      color: warnings ? "warning" : "success",
      message: `Génération lancée : WordPress recevra l’article avec le statut demandé une fois généré.${warnings}`,
    };
  }
  return {
    color: "warning",
    message: `Brouillon RMR créé avec ses contenus Affilizz. ${
      generation.label
    }${generation.message ? ` : ${generation.message}` : "."}`,
  };
};

// Fields of the product snapshot frozen with the draft (backend contract).
const SNAPSHOT_FIELDS = [
  "merchant",
  "merchantProductId",
  "productLocalizedId",
  "title",
  "sourceUrl",
  "canonicalUrl",
  "brand",
  "imageUrl",
  "priceCurrent",
  "priceOriginal",
  "currency",
  "availability",
  "seller",
  "rating",
  "reviewCount",
  "features",
  "provenance",
];

/** Product as validated at launch, manual edits included, without UI fields. */
export const toProductSnapshot = (product) =>
  SNAPSHOT_FIELDS.reduce((snapshot, field) => {
    const value = product[field];
    if (value !== undefined && value !== null && value !== "") {
      snapshot[field] = clone(value);
    }
    return snapshot;
  }, {});
